import type { UseFormRegister, FieldErrors, FieldValues, Path } from "react-hook-form";
import type { IndustryOption } from "@app/shared";
import { Button, Field, Input, Select, Textarea } from "../../components/ui";
import { LocationPicker, type Coords } from "../../components/map/CompanyMap";

// 業種プルダウンの状態（§3.3）。フォームでは業種は必須のため、取得失敗/0件では送信できない
// （親は industry.query の isError/isLoading で送信ボタンを disabled にする）。
export interface IndustrySelectState {
  isLoading: boolean;
  isError: boolean;
  data: IndustryOption[] | undefined;
  refetch: () => void;
  // 編集時のみ: 現在値（廃止済みだと選択肢に無いので先頭に補って表示する）。
  currentCode?: string;
  currentLabel?: string;
}

// 企業の受入情報フィールド（自己登録・代理登録・受入情報編集で共有）。
// jobTypes はカンマ区切りテキストで受け取り、呼び出し側で string[] に変換する。
//
// 呼び出し側3つ（自己登録・代理登録・編集）で値型が異なるため、フォーム値型 T の
// ジェネリックにする。UseFormRegister<T> は T に対して反変なので、FieldValues や
// any で受けると呼び出し側の型が通らない（または型検査が消える）。
// 下記の各フィールド名は3つの値型すべてに存在するが、TS は任意の T についてそれを
// 知り得ないため、Path<T> への変換は r() の1箇所に閉じ込める。

export function CompanyProfileFields<T extends FieldValues>({
  register,
  errors,
  industry,
  location,
}: {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  // 業種プルダウン（§3.3）。マスタから1つ選ぶ（自由入力不可）。
  industry: IndustrySelectState;
  // 所在地の地図ピッカー（F-16・§2.6）。親が RHF の watch/setValue で配線する。
  location?: {
    value: Coords;
    onChange: (v: { latitude: number; longitude: number } | null) => void;
  };
}) {
  const r = (k: string) => register(k as Path<T>);
  const msg = (k: string) =>
    ((errors as FieldErrors)[k]?.message as string | undefined) ?? undefined;

  // 現在値が非活性（選択肢に無い）なら先頭に補って表示する（編集時のみ・§3.3）。
  const options = industry.data ?? [];
  const currentInactive =
    industry.currentCode != null && !options.some((o) => o.code === industry.currentCode);

  return (
    <>
      <Field label="企業名" error={msg("name")}>
        <Input {...r("name")} />
      </Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="所在地（エリア）" error={msg("area")}>
          <Input {...r("area")} placeholder="例: 苫小牧市表町" />
        </Field>
        <Field label="業種" error={msg("industryCode")}>
          {industry.isLoading ? (
            <Select disabled>
              <option>読込中…</option>
            </Select>
          ) : industry.isError ? (
            <div className="space-y-1">
              <p className="text-xs font-bold text-rose-500">
                業種の取得に失敗しました。保存するには再試行してください。
              </p>
              <Button type="button" variant="ghost" onClick={() => industry.refetch()}>
                再試行
              </Button>
            </div>
          ) : options.length === 0 && !currentInactive ? (
            <p className="text-xs font-bold text-rose-500">
              業種が未設定です。管理者にご連絡ください。
            </p>
          ) : (
            <>
              <Select
                {...register("industryCode" as Path<T>, { required: "業種を選択してください" })}
              >
                <option value="" disabled>
                  選択してください
                </option>
                {currentInactive && (
                  <option value={industry.currentCode}>
                    {industry.currentLabel}（現在の値・廃止済み）
                  </option>
                )}
                {options.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.label}
                  </option>
                ))}
              </Select>
              {currentInactive && (
                <span className="mt-1 block text-xs text-amber-600">
                  この業種は廃止されています。別の値に変更すると元に戻せません。
                </span>
              )}
            </>
          )}
        </Field>
      </div>
      <Field label="職種（カンマ区切り）" error={msg("jobTypesText")} hint="例: 接客, 品出し">
        <Input {...r("jobTypesText")} placeholder="接客, 品出し" />
      </Field>
      <Field label="仕事内容の説明" error={msg("description")}>
        <Textarea {...r("description")} />
      </Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="受入可能人数（一度に受け入れられる上限）" error={msg("capacity")}>
          <Input type="number" min={1} {...r("capacity")} />
        </Field>
        <Field label="担当者名" error={msg("contactName")}>
          <Input {...r("contactName")} />
        </Field>
      </div>
      <Field label="条件（服装・持ち物など）" error={msg("conditions")}>
        <Textarea {...r("conditions")} placeholder="例: 動きやすい服装・運動靴" />
      </Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="担当者メール" error={msg("contactEmail")}>
          <Input type="email" {...r("contactEmail")} />
        </Field>
        <Field label="担当者電話（任意）" error={msg("contactPhone")}>
          <Input {...r("contactPhone")} />
        </Field>
      </div>
      {location && (
        <Field
          label="所在地の地図（任意）"
          hint="地図をクリックしてピンを置くと、学校に見つけてもらいやすくなります"
        >
          <LocationPicker value={location.value} onChange={location.onChange} />
        </Field>
      )}
    </>
  );
}

// フォーム値 → API入力（jobTypesText を配列へ）
export function splitJobTypes(text: string): string[] {
  return text
    .split(/[,、]/)
    .map((s) => s.trim())
    .filter(Boolean);
}
